import { memo, useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Receipt, Upload, Loader2, Search, ChevronLeft, ChevronRight, Clock, Camera, Plus, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import FacturaDialog from './FacturaDialog';
import FacturaManualForm from './FacturaManualForm';
import SimilarSuppliersPanel from './SimilarSuppliersPanel';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const MONTHS = ['', 'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];
export const lei = (v) => `${(v ?? 0).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const fmtD = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '—');
const ALL = '__all__';
const sum = (rows, k) => Math.round(rows.reduce((a, r) => a + (r[k] || 0), 0) * 100) / 100;
const onlyInvoices = (rows) => rows.filter((r) => Math.abs(r.total || 0) > 0.005);
export const SagaBadge = ({ r }) => (r.sursa === 'manual' ? (
  r.saga_linked
    ? <span className="ml-1.5 font-sans text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 uppercase" title={`Introdus manual, împerecheat cu Saga${r.manual_snapshot?.total != null && Math.abs(r.manual_snapshot.total - r.total) > 0.005 ? ` (total manual ${lei(r.manual_snapshot.total)})` : ''}`} data-testid={`factura-saga-linked-${r.id}`}>manual + Saga ✓</span>
    : <span className="ml-1.5 font-sans text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 uppercase" title="Introdus manual, neconfirmat în Saga" data-testid={`factura-manual-${r.id}`}>manual</span>
) : null);

export const InvoiceTable = ({ rows, showSupplier = true, testId = 'facturi-table', onSupplierClick, onOpenInvoice, monthlyBalance }) => {
  const colCount = showSupplier ? 13 : 12;
  const soldCls = (v) => (v == null ? 'text-slate-400' : Math.abs(v) < 0.005 ? 'text-green-700' : v > 0 ? 'text-blue-700' : 'text-red-600');
  const soldTxt = (v) => (v == null ? '—' : Math.abs(v) < 0.005 ? '0,00' : v > 0 ? `+${lei(v)}` : `−${lei(-v)}`);
  return (
  <Table data-testid={testId}>
    <TableHeader>
      <TableRow>
        <TableHead>Data</TableHead>
        <TableHead>Tip</TableHead>
        <TableHead>Nr. document</TableHead>
        {showSupplier && <TableHead>Furnizor</TableHead>}
        <TableHead className="text-right">Bază</TableHead>
        <TableHead className="text-right">TVA</TableHead>
        <TableHead className="text-right">Total</TableHead>
        <TableHead>Scadent</TableHead>
        <TableHead className="text-right">Plată efectivă</TableHead>
        <TableHead className="text-right">Diferență</TableHead>
        <TableHead className="text-center"><Camera className="w-4 h-4 inline" /></TableHead>
        <TableHead>Observații</TableHead>
        <TableHead className="text-right">Neachitat<br /><span className="text-[10px] font-normal">Saga</span></TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {rows.flatMap((r, i) => {
        const month = (r.data || '').slice(0, 7);
        const prevMonth = i > 0 ? (rows[i - 1].data || '').slice(0, 7) : null;
        const nodes = [];
        if (monthlyBalance && i > 0 && month && month !== prevMonth) {
          nodes.push(
            <TableRow key={`sep-${month}`} className="bg-slate-100/80 border-y-2 border-slate-300" data-testid={`fisa-sep-${month}`}>
              <TableCell colSpan={colCount} className="py-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold uppercase tracking-wide text-slate-500">Sold {MONTHS[parseInt(month.slice(5, 7), 10)]} {month.slice(0, 4)}</span>
                  <span className={`font-semibold ${soldCls(monthlyBalance[month])}`}>{soldTxt(monthlyBalance[month])} lei</span>
                </div>
              </TableCell>
            </TableRow>
          );
        }
        nodes.push(
        <TableRow key={r.id} data-testid={`factura-row-${r.nr_nir || r.id}`} className={onOpenInvoice ? 'cursor-pointer hover:bg-slate-50' : ''} onClick={() => onOpenInvoice?.(r)}>
          <TableCell className="text-sm whitespace-nowrap">{fmtD(r.data)}</TableCell>
          <TableCell className="whitespace-nowrap">{(r.tip_doc || 'Factură') === 'Factură' ? <span className="text-xs text-slate-500">Factură</span> : <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 font-medium" data-testid={`factura-tip-${r.id}`}>{r.tip_doc}</span>}</TableCell>
          <TableCell className="font-mono text-xs whitespace-nowrap">{r.nr_intrare}<SagaBadge r={r} /></TableCell>
          {showSupplier && (
            <TableCell className="text-sm">
              {onSupplierClick ? (
                <button type="button" className="text-left text-blue-700 hover:underline font-medium" onClick={(e) => { e.stopPropagation(); onSupplierClick(r.cod); }} data-testid={`factura-supplier-link-${r.nr_nir}`}>{r.denumire}</button>
              ) : r.denumire}
            </TableCell>
          )}
          <TableCell className="text-right text-sm whitespace-nowrap">{lei(r.baza_tva)}</TableCell>
          <TableCell className="text-right text-sm whitespace-nowrap">{lei(r.tva)}</TableCell>
          <TableCell className={`text-right text-sm font-medium whitespace-nowrap ${r.total < 0 ? 'text-blue-700' : ''}`}>{lei(r.total)}</TableCell>
          <TableCell className="text-sm whitespace-nowrap">{fmtD(r.scadent)}</TableCell>
          <TableCell className="text-right text-sm whitespace-nowrap" data-testid={`factura-platit-${r.id}`}>
            {r.total < 0 ? (
              <span className="text-xs text-violet-700" title={r.compensat_in?.length ? `Alocat pe: ${r.compensat_in.map((c) => c.nr).join(', ')}` : 'Credit nealocat — alocă-l din „Înregistrează plată” (sursa: storno)'} data-testid={`factura-storno-${r.id}`}>storno{r.platit > 0 ? ` · alocat ${lei(r.platit)}` : ''}{r.credit_ramas > 0.005 ? ` · credit ${lei(r.credit_ramas)}` : ''}</span>
            ) : r.diferenta != null ? (
              <span className="inline-flex items-center gap-1">
                <span className="text-green-700 font-semibold">{lei(r.platit)}</span>
                {r.compensat > 0 && <span className="text-[10px] text-violet-700" title="din care compensat cu storno" data-testid={`factura-compensat-${r.id}`}>({lei(r.compensat)} storno)</span>}
              </span>
            ) : <span className="text-slate-300">—</span>}
          </TableCell>
          <TableCell className="text-right text-sm whitespace-nowrap" data-testid={`factura-diferenta-${r.id}`}>
            {r.total < 0 || r.diferenta == null ? '' : Math.abs(r.diferenta) < 0.005 ? <span className="text-green-700 font-semibold">0</span> : r.diferenta > 0 ? <span className="text-blue-700 font-semibold">+{lei(r.diferenta)}</span> : <span className="text-red-600 font-semibold">−{lei(-r.diferenta)}</span>}
          </TableCell>
          <TableCell className="text-center text-xs text-slate-500">{r.nr_poze > 0 ? <span className="inline-flex items-center gap-0.5" data-testid={`factura-poze-${r.nr_nir}`}><Camera className="w-3.5 h-3.5" />{r.nr_poze}</span> : ''}</TableCell>
          <TableCell className="text-xs text-slate-500 max-w-[220px] truncate" title={r.inf_suplm || ''}>{r.inf_suplm || ''}</TableCell>
          <TableCell className={`text-right text-sm whitespace-nowrap ${r.neachitat > 0 ? 'text-slate-800 font-medium' : 'text-slate-400'}`} data-testid={`factura-neachitat-${r.id}`}>{r.sursa === 'manual' && !r.saga_linked ? <span className="text-[10px]" title="Document manual, neconfirmat în Saga">nu e în Saga</span> : r.neachitat > 0 ? lei(r.neachitat) : <span title="Achitată în Saga">—</span>}</TableCell>
        </TableRow>
        );
        return nodes;
      })}
    </TableBody>
    <TableFooter>
      <TableRow className="bg-slate-100 font-semibold" data-testid={`${testId}-totals`}>
        <TableCell colSpan={showSupplier ? 4 : 3} className="text-sm">TOTAL documente ({onlyInvoices(rows).length}){rows.length !== onlyInvoices(rows).length ? <span className="font-normal text-slate-500"> · {rows.length - onlyInvoices(rows).length} fără sumă (neincluse)</span> : null}</TableCell>
        <TableCell className="text-right text-sm whitespace-nowrap" data-testid={`${testId}-total-baza`}>{lei(sum(onlyInvoices(rows), 'baza_tva'))}</TableCell>
        <TableCell className="text-right text-sm whitespace-nowrap" data-testid={`${testId}-total-tva`}>{lei(sum(onlyInvoices(rows), 'tva'))}</TableCell>
        <TableCell className="text-right text-sm whitespace-nowrap" data-testid={`${testId}-total-total`}>{lei(sum(onlyInvoices(rows), 'total'))}</TableCell>
        <TableCell />
        <TableCell className="text-right text-sm whitespace-nowrap text-green-700" data-testid={`${testId}-total-platit`}>{lei(sum(onlyInvoices(rows).filter((r) => r.total > 0), 'platit'))}</TableCell>
        <TableCell className="text-right text-sm whitespace-nowrap" data-testid={`${testId}-total-diferenta`}>{(() => { const d = Math.round((sum(onlyInvoices(rows).filter((r) => r.total > 0), 'platit') - sum(onlyInvoices(rows).filter((r) => r.total > 0), 'total') + sum(onlyInvoices(rows).filter((r) => r.total < 0), 'credit_ramas')) * 100) / 100; return Math.abs(d) < 0.005 ? <span className="text-green-700">0</span> : d > 0 ? <span className="text-blue-700">+{lei(d)}</span> : <span className="text-red-600">−{lei(-d)}</span>; })()}</TableCell>
        <TableCell colSpan={2} />
        <TableCell className="text-right text-sm whitespace-nowrap text-slate-800" data-testid={`${testId}-total-neachitat`}>{lei(sum(onlyInvoices(rows), 'neachitat'))}</TableCell>
      </TableRow>
    </TableFooter>
  </Table>
  );
};

const CheltuieliFurnizoriTab = memo(function CheltuieliFurnizoriTab({ canEdit = true, onOpenSupplier }) {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [imports, setImports] = useState([]);
  const [cod, setCod] = useState(ALL);
  const [stare, setStare] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [similar, setSimilar] = useState([]);
  const refreshSimilar = async () => { try { const r = await axios.get(`${API_URL}/furnizori/sugestii-unire`, auth()); setSimilar(r.data); } catch { /* ignore */ } };
  const fileRef = useRef(null);
  const luna = `${year}-${String(month).padStart(2, '0')}`;

  const [tipDoc, setTipDoc] = useState(ALL);
  const [tipuri, setTipuri] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { luna, stare: stare || undefined, q: q || undefined, cod: cod === ALL ? undefined : cod, tip_doc: tipDoc === ALL ? undefined : tipDoc };
      const [f, s, i, td] = await Promise.all([
        axios.get(`${API_URL}/furnizori/facturi`, { ...auth(), params }),
        axios.get(`${API_URL}/furnizori`, auth()),
        axios.get(`${API_URL}/furnizori/imports`, auth()),
        axios.get(`${API_URL}/furnizori/tipuri-doc`, auth()),
      ]);
      setData(f.data); setSuppliers(s.data); setImports(i.data); setTipuri(td.data);
    } catch { toast.error('Nu s-au putut încărca documentele'); }
    finally { setLoading(false); }
  }, [luna, stare, q, cod, tipDoc]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const shift = (d) => { let m = month + d, y = year; if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; } setMonth(m); setYear(y); };

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await axios.post(`${API_URL}/furnizori/import`, fd, auth());
      const d = r.data;
      const extra = [d.linked ? `${d.linked} împerecheate cu documente manuale` : '', d.merged_suppliers?.length ? `furnizori uniți: ${d.merged_suppliers.join(', ')}` : ''].filter(Boolean);
      toast.success(`${d.rows} rânduri · ${d.added} noi · ${d.skipped} existente (sărite)${d.updated ? ` · ${d.updated} actualizate la plată` : ''}${extra.length ? ` · ${extra.join(' · ')}` : ''}`, { duration: 9000 });
      (d.warnings || []).forEach((w) => toast.warning(w, { duration: 12000 }));
      setSimilar(d.similar_suppliers || []);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare la import'); }
    finally { setImporting(false); }
  };

  const rows = data?.rows || [];
  const t = data?.totals || {};
  const last = imports[0];

  return (
    <div className="space-y-4" data-testid="cheltuieli-furnizori-tab">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><Receipt className="w-5 h-5 text-slate-600" /> Documente furnizori</h2>
          <p className="text-sm text-slate-500">Facturi importate din Saga (SPV) plus documente adăugate manual (borderouri, polițe, taxe, contracte…). Orice document cu sumă intră în total și poate primi plăți. Click pe un document pentru poze și plăți; click pe numele furnizorului pentru fișa lui.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 border rounded-lg px-1 bg-white">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(-1)} data-testid="facturi-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-medium min-w-[140px] text-center" data-testid="facturi-month-label">{MONTHS[month]} {year}</span>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(1)} data-testid="facturi-next-month"><ChevronRight className="w-4 h-4" /></Button>
          </div>
          {canEdit && (
            <>
              <input ref={fileRef} type="file" accept=".xls,.xlsx,.ods,.csv" className="hidden" onChange={onFile} data-testid="facturi-file-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="facturi-import-btn">
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Import Saga
              </Button>
              <Button onClick={() => setAdding(true)} data-testid="facturi-add-manual-btn"><Plus className="w-4 h-4 mr-2" /> Adaugă document manual</Button>
            </>
          )}
        </div>
      </div>

      <SimilarSuppliersPanel items={similar} onClose={() => setSimilar([])} onChanged={() => { load(); refreshSimilar(); }} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="facturi-totals">
        {[['Documente', t.facturi ?? 0, ''], ['Bază', lei(t.baza_tva), 'lei'], ['TVA', lei(t.tva), 'lei'], ['Total documente', lei(t.total), 'lei']].map(([l, v, u]) => (
          <div key={l} className="rounded-lg border bg-white px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-slate-400">{l}</div>
            <div className="text-lg font-semibold">{v} <span className="text-xs font-normal text-slate-400">{u}</span></div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-slate-500">
        {t.neachitat > 0 && <span className="text-slate-800 font-medium" data-testid="facturi-unpaid-total">Neachitat Saga: {lei(t.neachitat)} lei</span>}
        {t.platit > 0 && <span className="text-green-700 font-medium" data-testid="facturi-paid-total">Plată efectivă: {lei(t.platit)} lei</span>}
        {t.platit > 0 && <span className={`font-medium ${Math.abs(t.diferenta) < 0.005 ? 'text-green-700' : t.diferenta > 0 ? 'text-blue-700' : 'text-red-600'}`} data-testid="facturi-diff-total">Diferență: {Math.abs(t.diferenta) < 0.005 ? '0' : t.diferenta > 0 ? `+${lei(t.diferenta)}` : `−${lei(-t.diferenta)}`} lei</span>}
        {t.credit_storno > 0 && <span className="text-violet-700 font-medium" data-testid="facturi-credit-storno">Credit storno nealocat: {lei(t.credit_storno)} lei</span>}
        {t.alte_documente?.count > 0 && <span className="text-slate-600" data-testid="facturi-other-docs">+ {t.alte_documente.count} documente fără sumă (contracte etc.) — nu intră în total</span>}
        {last && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Ultimul import: {new Date(last.imported_at).toLocaleString('ro-RO')} · {last.filename} ({last.added} noi / {last.skipped} sărite)</span>}
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="facturi-filters">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-2 top-2" />
          <Input className="h-8 w-64 pl-8 pr-7 text-sm bg-white" placeholder="Nr. factură / furnizor / observații..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="facturi-search" />
          {q && <button type="button" className="absolute right-1.5 top-1.5 p-0.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" onClick={() => setQ('')} title="Șterge căutarea" data-testid="facturi-search-clear"><X className="w-4 h-4" /></button>}
        </div>
        <div className="flex items-center gap-1">
          <Select value={cod} onValueChange={setCod}>
            <SelectTrigger className="h-8 w-72 text-sm bg-white" data-testid="facturi-supplier-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toți furnizorii</SelectItem>
              {suppliers.map((s) => <SelectItem key={s.cod} value={s.cod}>{s.denumire}</SelectItem>)}
            </SelectContent>
          </Select>
          {cod !== ALL && <button type="button" className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" onClick={() => setCod(ALL)} title="Toți furnizorii" data-testid="facturi-supplier-clear"><X className="w-4 h-4" /></button>}
        </div>
        <div className="flex items-center gap-1">
          <Select value={tipDoc} onValueChange={setTipDoc}>
            <SelectTrigger className="h-8 w-52 text-sm bg-white" data-testid="facturi-tip-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toate tipurile</SelectItem>
              {tipuri.map((tp) => <SelectItem key={tp} value={tp}>{tp}</SelectItem>)}
            </SelectContent>
          </Select>
          {tipDoc !== ALL && <button type="button" className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" onClick={() => setTipDoc(ALL)} title="Toate tipurile" data-testid="facturi-tip-clear"><X className="w-4 h-4" /></button>}
        </div>
        <div className="flex rounded-md border overflow-hidden text-xs bg-white" data-testid="facturi-stare-filter">
          {[['', 'Toate'], ['achitata', 'Achitate'], ['partial', 'Parțial'], ['fara', 'Fără plată']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setStare(k)} className={`px-3 py-1.5 ${stare === k ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid={`facturi-stare-${k || 'toate'}`}>{l}</button>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? <div className="flex items-center gap-2 text-slate-500 p-8"><Loader2 className="w-4 h-4 animate-spin" /> Se încarcă...</div>
            : rows.length === 0 ? <div className="p-10 text-center text-sm text-slate-500" data-testid="facturi-empty">Nicio factură pentru filtrele alese.</div>
            : <div className="overflow-x-auto"><InvoiceTable rows={rows} onSupplierClick={onOpenSupplier} onOpenInvoice={(r) => setOpenId(r.id)} /></div>}
        </CardContent>
      </Card>
      <FacturaDialog facturaId={openId} onClose={() => setOpenId(null)} onChanged={load} canEdit={canEdit} suppliers={suppliers} />
      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="max-w-2xl" data-testid="facturi-add-manual-dialog">
          <DialogHeader><DialogTitle>Adaugă document furnizor manual</DialogTitle><DialogDescription>Facturi de la instituții care nu ajung în SPV, borderouri și polițe de asigurare, contracte etc. Nu afectează importurile Saga.</DialogDescription></DialogHeader>
          <FacturaManualForm suppliers={suppliers} onCancel={() => setAdding(false)} onSaved={(r) => { setAdding(false); load(); setOpenId(r.id); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default CheltuieliFurnizoriTab;
